import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Clock, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "jotbay_trial_banner_dismissed";

export default function TrialBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === "1");

  // Single source of truth: reuse the backend-computed trial_days_left (same value the
  // sidebar + Billing page use) — no independent calculation here.
  const days = user?.trial_days_left;
  const show =
    user?.subscription_status === "trialing" &&
    typeof days === "number" &&
    days >= 0 &&
    days <= 3 &&
    !dismissed;

  if (!show) return null;

  const canManageBilling =
    user?.account_type !== "organization" ||
    user?.is_primary_admin === true ||
    user?.is_org_admin === true;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div
      className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3.5 text-amber-900"
      data-testid="trial-expiry-banner"
    >
      <div className="flex items-start gap-3">
        <Clock className="h-5 w-5 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm">{t("trialBanner.title")}</p>
          <p className="text-xs mt-0.5 leading-relaxed opacity-90" data-testid="trial-banner-days">
            {t("trialBanner.daysLeft", { count: days })} — {t("trialBanner.subtitle")}
          </p>
          {!canManageBilling && (
            <p className="text-[11px] mt-1 opacity-75" data-testid="trial-banner-admin-msg">
              {t("trialBanner.adminMsg")}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canManageBilling && (
            <Button
              asChild
              size="sm"
              className="h-8 text-xs bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]"
              data-testid="trial-banner-cta"
            >
              <Link to="/app/billing">{t("trialBanner.cta")}</Link>
            </Button>
          )}
          <button
            onClick={dismiss}
            aria-label={t("trialBanner.dismiss")}
            data-testid="trial-banner-dismiss"
            className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-amber-100 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
